import { useState, useCallback, useEffect } from 'react';
import {
	useInfiniteQuery,
	useMutation,
	keepPreviousData,
} from '@tanstack/react-query';

import {
	fetchPatients,
	sendSms,
	type PatientListItem,
	type PatientFilters,
} from '@/api/adapters/patients';
import { toast } from '@/app/store/toast.store';
import { ApiError } from '@/api/error';
import {
	maskNameKeepLast,
	maskPhone,
} from '@/pages/medical/screens/home/utils';

export type { PatientListItem };

const PAGE_SIZE = 50;

// View-All modal default sort = R-value desc (PRD 02 §3.5.5 — clinical priority).
const DEFAULT_FILTERS: Omit<PatientFilters, 'page' | 'size'> = {
	search: '',
	status: 'ALL',
	riskLevel: '',
	primaryDx: '',
	staff: '',
	dischargeMonths: '',
	delayedOnly: false,
	sortKey: 'riskScore',
	sortDir: 'desc',
};

export function usePatientsList() {
	const [filters, setFilters] =
		useState<Omit<PatientFilters, 'page' | 'size'>>(DEFAULT_FILTERS);

	const query = useInfiniteQuery({
		queryKey: ['patients', 'list', filters],
		queryFn: ({ pageParam }) =>
			fetchPatients({ ...filters, page: pageParam, size: PAGE_SIZE }),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.data.length >= PAGE_SIZE ? lastPage.page + 1 : undefined,
		staleTime: 60_000,
		placeholderData: keepPreviousData,
	});

	// PRD 02 §3.5.5: no pagination — the modal renders the full filtered/sorted set in one
	// scrollable area. The list endpoint is paged (50/req), so eagerly drain every page; the
	// BE sorts across the whole set, so concatenated pages stay globally ordered.
	// SHCT-466 — stop draining once a page fetch fails: hasNextPage stays true after a
	// failed fetchNextPage, so without this guard the effect would retry in a loop. The
	// error is surfaced via isError below so the modal shows retry instead of a partial list.
	useEffect(() => {
		if (
			query.hasNextPage &&
			!query.isFetchingNextPage &&
			!query.isFetchNextPageError
		) {
			query.fetchNextPage();
		}
	}, [
		query.hasNextPage,
		query.isFetchingNextPage,
		query.isFetchNextPageError,
		query.data,
	]);

	const patients = query.data?.pages.flatMap((p) => p.data) ?? [];
	const total = query.data?.pages[0]?.total ?? 0;

	const setFilter = useCallback(
		<K extends keyof PatientFilters>(key: K, value: PatientFilters[K]) => {
			setFilters((prev) => ({ ...prev, [key]: value }));
		},
		[],
	);

	const setSort = useCallback((key: string) => {
		setFilters((prev) => {
			if (prev.sortKey === key) {
				return {
					...prev,
					sortKey: key,
					sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc',
				};
			}
			const defaultDesc = [
				'riskScore',
				'appRegisteredAt',
				'dischargeDate',
			].includes(key);
			return { ...prev, sortKey: key, sortDir: defaultDesc ? 'desc' : 'asc' };
		});
	}, []);

	const resetFilters = useCallback(() => {
		// PRD 02 §3.5.10 — reset restores defaults incl. sort = R-value desc.
		setFilters(DEFAULT_FILTERS);
	}, []);

	return {
		patients,
		total,
		filters,
		setFilter,
		setSort,
		resetFilters,
		isLoading: query.isLoading,
		isFetchingNextPage: query.isFetchingNextPage,
		hasNextPage: query.hasNextPage,
		fetchNextPage: query.fetchNextPage,
		// SHCT-466 — surface a mid-pagination failure too, not just the initial load,
		// so the modal replaces the partial list with the error + retry state.
		isError: query.isError || query.isFetchNextPageError,
		refetch: query.refetch,
	};
}

export function useSendSms() {
	return useMutation({
		mutationFn: (params: { uid: string; name: string; phone: string }) =>
			sendSms(params.uid),
		onSuccess: (_data, { name, phone }) => {
			const maskedName = maskNameKeepLast(name);
			// Home delay panel doesn't carry a phone number — omit the "(number)" suffix when absent.
			const suffix = phone ? ` (${maskPhone(phone)})` : '';
			toast.success(`${maskedName} 환자에게 SMS 발송${suffix}`);
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error('SMS 발송에 실패했습니다.');
			}
		},
	});
}
